import React, { useEffect, useRef, useState } from "react";
import DashboardContainerHeader from "../../../Dashboard_en/Components/DashboardContainerHeader/DashboardContainerHeader";
import DashboardContainer from "../../../Dashboard_en/DashboardContainer";
import ApiName from "../../../ApiName";
import { toast } from "react-toastify";
import { Trash2, UploadCloud } from "lucide-react";
import { motion } from "framer-motion";
import axios from "axios";
import TextInput from "../../../Dashboard_en/Components/Inputs/TextInput/TextInput";
import TextAreaInput from "../../../Dashboard_en/Components/Inputs/TextAreaInput/TextAreaInput";

// Custom Tailwind File Upload Component
const TailwindFileUpload = ({ onFilesSelected, disabled }) => {
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setIsDragging(true);
    } else if (e.type === "dragleave") {
      setIsDragging(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (disabled) return;
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      onFilesSelected(e.dataTransfer.files);
    }
  };

  const handleChange = (e) => {
    e.preventDefault();
    if (disabled) return;
    
    if (e.target.files && e.target.files.length > 0) {
      onFilesSelected(e.target.files);
    }
  };

  return (
    <div 
      className={`border-2 border-dashed rounded-xl p-10 text-center transition-colors ${disabled ? 'opacity-50 cursor-not-allowed border-gray-300 bg-gray-50 dark:bg-gray-800' : isDragging ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900' : 'border-gray-300 hover:border-indigo-400 bg-white dark:bg-gray-800'}`}
      onDragEnter={handleDrag}
      onDragLeave={handleDrag}
      onDragOver={handleDrag}
      onDrop={handleDrop}
      onClick={() => !disabled && fileInputRef.current?.click()}
    >
      <input 
        type="file" 
        multiple 
        accept="image/*" 
        className="hidden" 
        ref={fileInputRef} 
        onChange={handleChange}
        disabled={disabled}
      />
      <div className="flex flex-col items-center justify-center space-y-3 cursor-pointer">
        <UploadCloud className="w-12 h-12 text-gray-400" />
        <p className="text-gray-600 dark:text-gray-300"><span className="font-semibold text-indigo-600 dark:text-indigo-400">Click to upload</span> or drag and drop</p>
      </div>
    </div>
  );
};

const SimpleTable = ({ data, columns }) => (
  <div className="overflow-hidden border border-gray-200 dark:border-gray-700 rounded-lg">
    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
      <thead className="bg-gray-50 dark:bg-gray-800">
        <tr>
          {columns.map((col, idx) => (
            <th key={idx} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              {col.header}
            </th>
          ))}
        </tr>
      </thead>
      <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
        {data.length === 0 ? (
          <tr>
            <td colSpan={columns.length} className="px-6 py-16 text-center text-gray-500">
              No data available
            </td>
          </tr>
        ) : (
          data.map((row, i) => (
            <tr key={i} className="hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
              {columns.map((col, idx) => (
                <td key={idx} className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                  {col.render ? col.render(row) : row[col.field]}
                </td>
              ))}
            </tr>
          ))
        )}
      </tbody>
    </table>
  </div>
);

const AdminHomeImage = () => {
  const [state, setState] = useState({ images: [] });
  const [homeData, setHomeData] = useState({
    quoteText: "",
    quoteAuthor: ""
  });
  const [isDisabled, setDisability] = useState(false);
  const [progressBarValue, setProgressBar] = useState(0);
  const timeoutIdRef = useRef(null);

  const loadImages = async () => {
    try {
      const response = await axios.get(`${ApiName}/HomeImage`);
      setState({ images: response.data });
    } catch (error) {
      console.log("failed to load images");
    }
  };

  const handlePaintRemove = async (id) => {
    try {
      await axios.delete(`${ApiName}/HomeImage/${id}`, {
        headers: { Authorization: localStorage.authToken }
      });
      loadImages();
      toast.success("Image successfully removed!");
    } catch (error) {
      toast.error("Failed to remove image");
    }
  };

  const uploadImageHandler = async (files) => {
    var form = new FormData();
    for (var i = 0; i < files.length; i++) {
      if (files[i].size > 4194304) {
        toast.error(`File ${files[i].name} exceeds the 4 MB limit.`);
        return;
      }
      form.append("files", files[i]);
    }

    setDisability(true);

    try {
      await axios.post(`${ApiName}/HomeImage`, form, {
        headers: {
          Authorization: localStorage.authToken
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setProgressBar(percentCompleted);
        }
      });
      setDisability(false);
      setProgressBar(0);
      toast.success("Images added successfully");
      loadImages();
    } catch (error) {
      setDisability(false);
      setProgressBar(0);
      toast.error("There was a problem uploading the files!");
    }
  };

  const loadHomeSettings = async () => {
    try {
      const response = await axios.get(`${ApiName}/Home`);
      setHomeData(response.data);
    } catch (error) {
      toast.error("There was a problem getting the home settings!");
    }
  };

  const handleQuoteTextChange = async () => {
    let doc = document.getElementById("quoteText");
    if (!doc || !doc.value) return;

    if (doc.value !== homeData.quoteText) {
      try {
        await axios.patch(`${ApiName}/Home/quote?text=${doc.value}`, null, {
          headers: { Authorization: localStorage.authToken }
        });
        loadHomeSettings();
        toast.success("Quote text changed successfully");
      } catch (error) {
        toast.error("Error changing quote text");
      }
    }
  };

  const handleQuoteAuthorChange = async () => {
    let doc = document.getElementById("quoteAuthor");
    if (!doc || !doc.value) return;

    if (doc.value !== homeData.quoteAuthor) {
      try {
        await axios.patch(`${ApiName}/Home/author?text=${doc.value}`, null, {
          headers: { Authorization: localStorage.authToken }
        });
        loadHomeSettings();
        toast.success("Quote author changed successfully");
      } catch (error) {
        toast.error("Error changing quote author");
      }
    }
  };

  useEffect(() => {
    loadImages();
    loadHomeSettings();

    const quoteTextInput = document.getElementById("quoteText");
    const quoteAuthorInput = document.getElementById("quoteAuthor");

    const onQuoteTextInput = () => {
      clearTimeout(timeoutIdRef.current);
      timeoutIdRef.current = setTimeout(() => {
        handleQuoteTextChange();
      }, 3000);
    };

    const onQuoteAuthorInput = () => {
      clearTimeout(timeoutIdRef.current);
      timeoutIdRef.current = setTimeout(() => {
        handleQuoteAuthorChange();
      }, 3000);
    };

    quoteTextInput?.addEventListener("input", onQuoteTextInput);
    quoteAuthorInput?.addEventListener("input", onQuoteAuthorInput);

    return () => {
      quoteTextInput?.removeEventListener("input", onQuoteTextInput);
      quoteAuthorInput?.removeEventListener("input", onQuoteAuthorInput);
      clearTimeout(timeoutIdRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const columns = [
    { header: "ID", field: "id" },
    { 
      header: "Image", 
      render: (row) => (
        <img
          src={`${ApiName}/${row.path}`}
          alt="home"
          className="w-20 object-cover rounded shadow-sm"
        />
      ) 
    },
    { 
      header: "Operations", 
      render: (row) => (
        <button
          onClick={() => handlePaintRemove(row.id)}
          title="Delete Image"
          className="p-2 text-white bg-red-500 rounded-lg hover:bg-red-600 transition-colors"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      ) 
    }
  ];

  return (
    <motion.div
      initial={{ opacity: 0, transition: { duration: 0.1 } }}
      animate={{ opacity: 1, transition: { duration: 0.5 } }}
      exit={{ opacity: 0, transition: { duration: 0.1 } }}
      className="grid grid-cols-1 gap-3 px-5 "
    >
      <DashboardContainer>
        <DashboardContainerHeader
          classNames={"text-zinc-900"}
          text="Image List"
        />
        <div className="px-5 py-5">
          <SimpleTable data={state.images} columns={columns} />
        </div>
      </DashboardContainer>

      <DashboardContainer>
        <DashboardContainerHeader
          classNames={"text-zinc-900"}
          text="Add Image"
        />
        
        {isDisabled && (
          <div className="px-5 py-5">
            <div className="w-full bg-gray-200 rounded-full h-2.5 dark:bg-gray-700">
              <div className="bg-indigo-600 h-2.5 rounded-full transition-all duration-300 ease-in-out" style={{ width: `${progressBarValue}%` }}></div>
            </div>
            <p className="text-center text-sm text-gray-500 mt-2">Uploading... {progressBarValue}%</p>
          </div>
        )}

        <div className="p-5">
          <p className="mb-3 text-xs text-gray-500 dark:text-gray-400">
            📎 Max <strong>4 MB</strong> per image — auto-converted to <strong>WebP</strong>
          </p>
          
          <TailwindFileUpload 
            disabled={isDisabled}
            onFilesSelected={uploadImageHandler}
          />
        </div>
      </DashboardContainer>

      <DashboardContainer>
        <DashboardContainerHeader
          classNames={"text-zinc-900"}
          text="Home Settings"
        />
        <div className="m-5 mb-10 space-y-4">
          <TextAreaInput
            value={homeData.quoteText || ""}
            label="Quote Text"
            id="quoteText"
          />
          <TextInput
            value={homeData.quoteAuthor || ""}
            label="Quote Author"
            id="quoteAuthor"
          />
        </div>
      </DashboardContainer>
    </motion.div>
  );
};

export default AdminHomeImage;

